import React, { useState } from 'react';
import { clsx } from 'clsx';

export interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  tabs: TabItem[];
  defaultTab?: string;
  onTabChange?: (tabId: string) => void;
  className?: string;
  /** Mantém as abas montadas (escondidas) — ex.: editor com alterações não salvas. */
  keepMounted?: boolean;
}

/**
 * Componente de abas reutilizável seguindo o design system
 */
export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  onTabChange,
  className,
  keepMounted = false
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id);

  const handleTabChange = (tabId: string) => {
    if (tabs.find(tab => tab.id === tabId)?.disabled) {
      return;
    }
    
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className={clsx('w-full', className)}>
      {/* Tab Navigation */}
      <div className="border-b border-slate-200 overflow-x-auto">
        <nav className="flex space-x-8 min-w-max" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              disabled={tab.disabled}
              className={clsx(
                'group relative min-w-0 flex-1 overflow-hidden py-4 px-4 text-center text-sm font-medium hover:bg-slate-50 focus:z-10',
                'border-b-2 transition-all duration-200',
                {
                  'border-blue-500 text-blue-600': activeTab === tab.id,
                  'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700': 
                    activeTab !== tab.id && !tab.disabled,
                  'text-slate-400 cursor-not-allowed': tab.disabled,
                }
              )}
            >
              <div className="flex items-center justify-center space-x-2">
                {tab.icon && (
                  <span className={clsx('flex-shrink-0', {
                    'text-blue-500': activeTab === tab.id,
                    'text-slate-400': activeTab !== tab.id
                  })}>
                    {tab.icon}
                  </span>
                )}
                <span className="truncate">{tab.label}</span>
              </div>
              
              {/* Active indicator */}
              {activeTab === tab.id && (
                <div className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-500" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {keepMounted
          ? tabs.map((tab) => (
              <div key={tab.id} className={tab.id === activeTab ? undefined : 'hidden'}>
                {tab.content}
              </div>
            ))
          : activeTabContent}
      </div>
    </div>
  );
};

/**
 * Componente simpler de abas para casos básicos
 */
interface SimpleTabsProps {
  children: React.ReactElement<TabPanelProps>[];
  defaultTab?: number;
  onTabChange?: (index: number) => void;
}

export const SimpleTabs: React.FC<SimpleTabsProps> = ({
  children,
  defaultTab = 0,
  onTabChange
}) => {
  const [activeIndex, setActiveIndex] = useState(defaultTab);

  const handleTabChange = (index: number) => {
    setActiveIndex(index);
    onTabChange?.(index);
  };

  return (
    <div className="w-full">
      <div className="border-b border-slate-200 overflow-x-auto">
        <nav className="flex space-x-8 min-w-max">
          {children.map((child, index) => (
            <button
              key={index}
              onClick={() => handleTabChange(index)}
              className={clsx(
                'py-4 px-2 border-b-2 font-medium text-sm transition-all duration-200',
                {
                  'border-blue-500 text-blue-600': activeIndex === index,
                  'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700': 
                    activeIndex !== index,
                }
              )}
            >
              {child.props.label}
            </button>
          ))}
        </nav>
      </div>
      
      <div className="mt-6">
        {children[activeIndex]}
      </div>
    </div>
  );
};

// Props para TabPanel
interface TabPanelProps {
  label: string;
  children: React.ReactNode;
}

export const TabPanel: React.FC<TabPanelProps> = ({ children }) => {
  return <div>{children}</div>;
};

export default Tabs;